import React from 'react';
import waveformImage from '../../../assets/quick-richuels-waves/wave-audio.png';
import { CheckBadgeIcon, LockIcon, PlayBadgeIcon, TaskPlayIcon } from './icons';
import { cx } from './helpers';
import { ctaLabel, isContentCompleted, isContentInProgress, trackKey } from './trackCardUtils';
import { cardWaveForIndex } from './waveAssets';
import styles from './RecommendedTracksSection.module.css';

function actionLabel(track, completedTrack) {
  if (completedTrack) return 'Completed';
  if (isContentInProgress(track) || Number(track?.completed_task_count) > 0) return 'Continue';
  return 'Start Now';
}

function actionClassName(completedTrack, inProgress) {
  if (completedTrack) return styles.reviewBtn;
  if (inProgress) return styles.continueBtn;
  return styles.startBtn;
}

export default function RecommendedTrackCard({
  track,
  index,
  selectedTrackId,
  onSelectTrack,
  onLockedTrack,
  isSlide = false,
  slideCount = 0,
}) {
  const id = trackKey(track, index);
  const locked = Boolean(track?.locked);
  const current = Boolean(track?.current);
  const completedTrack = isContentCompleted(track);
  const inProgress = isContentInProgress(track);
  const selected = selectedTrackId === id;
  const title = String(track?.title ?? '').trim() || 'Untitled track';
  const description = String(track?.description ?? '').trim();
  const label = actionLabel(track, completedTrack);
  const actionClass = actionClassName(completedTrack, inProgress);
  const cardClass = cx(
    styles.card,
    locked && styles.cardLocked,
    current && styles.cardCurrent,
    completedTrack && styles.cardCompleted,
    selected && styles.cardSelected,
  );

  return (
    <article
      className={cardClass}
      role={isSlide ? 'group' : 'listitem'}
      aria-roledescription={isSlide ? 'slide' : undefined}
      aria-label={isSlide ? `${index + 1} of ${slideCount}` : undefined}
      style={{ '--card-i': index }}
    >
      <div className={styles.cover}>
        <img
          src={cardWaveForIndex(index)}
          alt=""
          className={cx(styles.coverImg, styles.coverReady)}
          decoding="async"
        />
        <div className={styles.coverOverlay}>
          <div className={styles.coverHeading}>
            <span className={styles.number}>{index + 1}</span>
            <div className={styles.coverTitleWrap}>
              <p className={styles.coverTitle}>{title}</p>
            </div>
          </div>
          {current && !locked ? <span className={styles.currentBadge}>Current</span> : null}
          {locked ? (
            <span className={styles.lockBadge} aria-hidden="true">
              <LockIcon />
            </span>
          ) : null}
          <div className={styles.coverMeta}>
            <span className={styles.quickMark}>
              <span className={styles.quickPlay} aria-hidden="true">
                <TaskPlayIcon />
              </span>
              Quick Richuel
            </span>
            <img
              src={waveformImage}
              alt=""
              className={styles.waveform}
              decoding="async"
            />
          </div>
        </div>
      </div>

      <div className={styles.body}>
        <h3 className={styles.cardTitle} title={title}>{title}</h3>
        {description ? <p className={styles.cardDescription} title={description}>{description}</p> : null}
      </div>

      {locked ? (
        <button
          type="button"
          className={styles.lockedBtn}
          onClick={() => onLockedTrack?.(track)}
          aria-label={`${title}, locked. Complete the previous track to unlock.`}
        >
          <LockIcon />
          <span>Locked</span>
        </button>
      ) : (
        <button
          type="button"
          className={actionClass}
          onClick={() => onSelectTrack?.(track)}
          aria-pressed={selected}
          aria-label={`${ctaLabel(track)}: ${title}`}
        >
          {completedTrack ? (
            <CheckBadgeIcon className={styles.btnBadge} />
          ) : (
            <PlayBadgeIcon className={styles.btnBadge} />
          )}
          <span>{label}</span>
        </button>
      )}
    </article>
  );
}
